// src/components/home/Homepage.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getVillageRankings,
  getStreakLeaderboard,
  getLastDaysCompletions,
  getWeekCompletions,
  getIndiaDate,
  getIndiaDateString,
  checkVillageAchievements,
  getMotivationalMessages,
  getTodayCompletion,
  getTodayLekhiCompletion,
  recalculateStreak,
  isLekhiTestDay,
  getNextSundayTestNumber,
} from '../../services/firebase/firestoreService';
import { doc, getDoc } from 'firebase/firestore';
import './Homepage.css';
import DailyPlanPage from '../daily/DailyPlanPage';
import CampPlanPage from '../camp/CampPlanPage';
import LekhiDiagnosticPage from '../lekhi/LekhiDiagnosticPage';
import LekhiTodayPage from '../lekhi/LekhiTodayPage';
import LekhiSundayTestPage from '../lekhi/LekhiSundayTestPage';
import NotificationPrompt from '../NotificationPrompt';
import CelebrationPopup from '../achievements/CelebrationPopup';
import { db } from '../../services/firebase/config';

const FALLBACK_MESSAGE = {
  mr: 'प्रत्येक व्यायाम तुम्हाला स्वप्नाजवळ नेतो.',
  en: 'Every rep brings you closer to your dream.'
};

const getDayOfYear = () => {
  const today = new Date();
  return Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
};

const getWeekDays = () => {
  const labels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const india = getIndiaDate();
  const dayOfWeek = india.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  return labels.map((label, i) => {
    const date = new Date(india);
    date.setDate(india.getDate() + mondayOffset + i);
    return { label, date, dateStr: getIndiaDateString(date) };
  });
};

const shouldShowPrompt = (status, currentDay) => {
  if (status === 'subscribed' || status === 'permanently_declined') return false;
  if (status === 'never_asked' && currentDay >= 1) return true;
  if (status === 'asked_day1' && currentDay >= 3) return true;
  if (status === 'asked_day3' && currentDay >= 7) return true;
  return false;
};

const formatPastDate = (dateStr, language) => {
  const d = new Date(dateStr);
  const day = d.getDate();
  const monthsMr = ['जाने', 'फेब्रु', 'मार्च', 'एप्रिल', 'मे', 'जून', 'जुलै', 'ऑग', 'सप्टें', 'ऑक्टो', 'नोव्हें', 'डिसें'];
  const monthsEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const months = language === 'mr' ? monthsMr : monthsEn;
  return `${day} ${months[d.getMonth()]}`;
};

const Homepage = ({ setActiveTab, setHideBottomNav }) => {
  const { currentUser, userData, refreshUserData } = useAuth();
  const [language, setLanguage] = useState(() =>
    localStorage.getItem('spocademy_language') || 'mr'
  );
  const [villageRankings, setVillageRankings] = useState([]);
  const [loadingRankings, setLoadingRankings] = useState(true);
  const [leaderboardView, setLeaderboardView] = useState('village');
  const [streakLeaderboard, setStreakLeaderboard] = useState(null);
  const [loadingStreakLeaderboard, setLoadingStreakLeaderboard] = useState(false);
  const [weekCompletions, setWeekCompletions] = useState({});
  const [pastCompletions, setPastCompletions] = useState([]);
  const [showAchievementBanner, setShowAchievementBanner] = useState(false);
  const [achievementBannerText, setAchievementBannerText] = useState('');
  const [currentView, setCurrentView] = useState('homepage');
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [asyncCanTrainToday, setCanTrainToday] = useState(true);
  const [asyncCanLekhiToday, setCanLekhiToday] = useState(true);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [motivationalMessage, setMotivationalMessage] = useState(FALLBACK_MESSAGE);
  const [streakJustUpdated, setStreakJustUpdated] = useState(false);
  const [displayStreak, setDisplayStreak] = useState(0);
  const [sundayTestNumber, setSundayTestNumber] = useState(null);
  const [sundayTestStatus, setSundayTestStatus] = useState(null);
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const bannerTimerRef = useRef(null);
  const streakPulseTimerRef = useRef(null);
  const scrollRef = useRef(null);

  const translations = {
    en: {
      points: 'Points',
      completed: '✓ Completed',
      todayTraining: "Today's Training",
      vyayam: 'Workout',
      lekhi: 'Lekhi',
      day: 'Day',
      tomorrow: 'Tomorrow',
      dayAfter: 'Day After',
      fitnessCompleted: '✓ Fitness Done',
      lekhiCompleted: '✓ Lekhi Done',
      bothCompleted: '✓ Fitness + Lekhi Done',
      locked: 'Locked',
      completedToday: 'Unlocks Tomorrow',
      villageRankings: 'Village Rankings',
      streakRankings: 'Streak Rankings',
      pts: 'pts',
      camp: 'Camp',
      diagnostic: 'Lekhi Trial',
      achievementSingle: '🏆 New achievement unlocked!',
      achievementMultiple: (n) => `🏆 ${n} new achievements unlocked!`,
      viewAchievements: 'View',
      todaysTest: "Weekly\nTest",
      testCompleted: '✓ Test Done',
      chooseLevel: 'Choose level'
    },
    mr: {
      points: 'गुण',
      completed: '✓ पूर्ण',
      todayTraining: 'आजचे प्रशिक्षण',
      vyayam: 'व्यायाम',
      lekhi: 'लेखी',
      day: 'Day',
      tomorrow: 'उद्या',
      dayAfter: 'परवा',
      fitnessCompleted: '✓ फिटनेस पूर्ण',
      lekhiCompleted: '✓ लेखी पूर्ण',
      bothCompleted: '✓ फिटनेस + लेखी पूर्ण',
      locked: 'बंद',
      completedToday: 'उद्या उपलब्ध होईल',
      villageRankings: 'गावांची क्रमवारी',
      streakRankings: 'स्ट्रीक क्रमवारी',
      pts: 'गुण',
      camp: 'कॅम्प',
      diagnostic: 'लेखी चाचणी',
      achievementSingle: '🏆 नवीन उपलब्धी मिळाली!',
      achievementMultiple: (n) => `🏆 ${n} नवीन उपलब्धी मिळाल्या!`,
      viewAchievements: 'बघा',
      todaysTest: 'साप्ताहिक\nपरीक्षा',
      testCompleted: '✓ परीक्षा पूर्ण',
      chooseLevel: 'स्तर निवडा'
    }
  };

  const t = translations[language];

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
  };

  const handleLeaderboardToggle = async (view) => {
    setLeaderboardView(view);
    if (view === 'streak' && streakLeaderboard === null && !loadingStreakLeaderboard) {
      setLoadingStreakLeaderboard(true);
      try {
        const data = await getStreakLeaderboard(currentUser?.uid, userData?.rankScore || 0);
        setStreakLeaderboard(data);
      } catch (e) {
      } finally {
        setLoadingStreakLeaderboard(false);
      }
    }
  };

  const formatStreakName = (user) => {
    const first = user.firstName || '';
    const lastInitial = user.lastName ? `${user.lastName.charAt(0)}.` : '';
    return `${first} ${lastInitial}`.trim();
  };

  const firstName = userData?.firstName || '';
  const fitnessCurrentDay = userData?.fitnessCurrentDay || 1;
  const lekhiCurrentDay = Math.min(userData?.lekhiPlanDay || 1, 60);
  const lekhiNeedsLevelPick = (userData?.lekhiPlanDay || 1) > 60;
  const points = (userData?.fitnessPoints || 0) + (userData?.lekhiPoints || 0);
  const userVillage = userData?.village || '';
  const unlockedCamps = userData?.unlockedCamps || [];
  const attendedCamps = userData?.attendedCamps || [];
  const diagnosticCompleted = userData?.diagnosticCompleted === true;

  const fitnessUnlocked = attendedCamps.length > 0 || userData?.fitnessOverrideUnlocked === true;
  const lekhiUnlocked = diagnosticCompleted || userData?.lekhiOverrideUnlocked === true;

  // The completion stamps live on the profile itself, so they are known at the
  // same instant as the day number — there is no separate lookup and therefore
  // no window in which the next day's button can be drawn. The async check is
  // kept as a second opinion: it can only ever hide a button, never reveal one.
  const todayDateStr = getIndiaDateString();
  const fitnessDoneStamp = userData?.lastFitnessCompletedDate === todayDateStr;
  const lekhiDoneStamp = userData?.lastLekhiCompletedDate === todayDateStr;
  const canTrainToday = !fitnessDoneStamp && asyncCanTrainToday;
  const canLekhiToday = !lekhiDoneStamp && asyncCanLekhiToday;

  // Whether today is a Sunday-test day is knowable the instant the profile
  // loads (start date + diagnostic flag are already on userData) — no
  // database call needed. This is what stops the Fitness/Lekhi buttons from
  // ever appearing on a Sunday, even for a moment.
  const instantSundayTest = useMemo(
    () => isLekhiTestDay(userData),
    [userData]
  );

  useEffect(() => {
    if (setHideBottomNav) {
      setHideBottomNav(currentView === 'lekhidiagnostic' || currentView === 'sundaytest');
    }
    return () => {
      if (setHideBottomNav) setHideBottomNav(false);
    };
  }, [currentView, setHideBottomNav]);

  const loadHomepageData = useCallback(async () => {
    if (!userData?.id) return;
    try {
      const [rankings, weekData, pastData, fitnessToday, lekhiToday, recalculatedStreak] = await Promise.all([
        getVillageRankings(),
        getWeekCompletions(userData.id),
        getLastDaysCompletions(userData.id, 3),
        getTodayCompletion(userData.id),
        getTodayLekhiCompletion(userData.id),
        recalculateStreak(userData.id)
      ]);
      setVillageRankings(rankings);
      setWeekCompletions(weekData);
      setPastCompletions(pastData);
      const fitnessDoneToday = fitnessToday?.allTasksCompleted === true;
      const lekhiDoneToday = lekhiToday !== null;
      setCanTrainToday(!fitnessDoneToday);
      setCanLekhiToday(!lekhiDoneToday);
      setDisplayStreak(recalculatedStreak);

      // Sunday test detection
      if (isLekhiTestDay(userData)) {
        const testNum = await getNextSundayTestNumber(userData.id);
        const todayIndiaStr = getIndiaDateString();
        const lastTestDoc = await getDoc(doc(db, 'lekhiTestResults', `${userData.id}_test${testNum - 1}`));
        const lastTestData = lastTestDoc.exists() ? lastTestDoc.data() : null;
        let takenToday = false;
        if (lastTestData?.submittedAt?.seconds) {
          const submittedDate = new Date(lastTestData.submittedAt.seconds * 1000);
          const submittedIndia = new Date(submittedDate.getTime() + (submittedDate.getTimezoneOffset() * 60000) + (5.5 * 60 * 60 * 1000));
          takenToday = getIndiaDateString(submittedIndia) === todayIndiaStr;
        }
        if (takenToday) {
          setSundayTestNumber(testNum - 1);
          setSundayTestStatus('completed');
        } else {
          const progressDocId = `${userData.id}_test${testNum}`;
          const progressSnap = await getDoc(doc(db, 'lekhiTestProgress', progressDocId));
          if (progressSnap.exists()) {
            const data = progressSnap.data();
            const endsAtMs = data.endsAt?.seconds ? data.endsAt.seconds * 1000 : 0;
            const isExpired = endsAtMs > 0 && Date.now() > endsAtMs;
            if (!data.submitted && !isExpired) {
              setSundayTestNumber(testNum);
              setSundayTestStatus('available');
              setCurrentView('sundaytest');
              return;
            }
          }
          setSundayTestNumber(testNum);
          setSundayTestStatus('available');
        }
      } else {
        setSundayTestNumber(null);
        setSundayTestStatus(null);
      }
    } catch (e) {
    } finally {
      setLoadingRankings(false);
    }
  }, [userData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadHomepageData();
  }, [loadHomepageData]);

  useEffect(() => {
    const loadMotivationalMessage = async () => {
      try {
        const all = await getMotivationalMessages();
        const active = all.filter(m => m.active === true);
        if (active.length === 0) { setMotivationalMessage(FALLBACK_MESSAGE); return; }
        const index = getDayOfYear() % active.length;
        setMotivationalMessage(active[index]);
      } catch (e) {
        setMotivationalMessage(FALLBACK_MESSAGE);
      }
    };
    loadMotivationalMessage();
  }, []);

  useEffect(() => {
    if (streakJustUpdated) {
      streakPulseTimerRef.current = setTimeout(() => setStreakJustUpdated(false), 5000);
    }
    return () => clearTimeout(streakPulseTimerRef.current);
  }, [streakJustUpdated]);

  useEffect(() => {
    if (!currentUser?.uid || !userData) return;
    const sessionKey = `notifPromptShown_${currentUser.uid}`;
    if (sessionStorage.getItem(sessionKey)) return;
    const status = userData.notificationPromptStatus || 'never_asked';
    if (shouldShowPrompt(status, fitnessCurrentDay)) {
      setShowNotificationPrompt(true);
      sessionStorage.setItem(sessionKey, 'true');
    }
  }, [currentUser?.uid, userData, fitnessCurrentDay]);

  const handleNotificationClose = async () => {
    setShowNotificationPrompt(false);
  };

  const handleCelebrationClose = () => {
    setCelebrationQueue(prev => prev.slice(1));
  };

  useEffect(() => {
    if (!currentUser?.uid) return;
    const key = `newAchievements_${currentUser.uid}`;
    const pending = JSON.parse(localStorage.getItem(key) || '[]');
    if (pending.length > 0) {
      const text = pending.length === 1 ? t.achievementSingle : t.achievementMultiple(pending.length);
      setAchievementBannerText(text);
      setShowAchievementBanner(true);
      localStorage.removeItem(key);
    }

    const celKey = `celebrateAchievements_${currentUser.uid}`;
    const pendingCel = JSON.parse(localStorage.getItem(celKey) || '[]');
    if (pendingCel.length > 0) {
      setCelebrationQueue(pendingCel);
      localStorage.removeItem(celKey);
    }
  }, [currentUser?.uid, currentView, t]);

  useEffect(() => {
    if (!currentUser?.uid || !userVillage) return;
    const checkVillage = async () => {
      try {
        const newlyUnlocked = await checkVillageAchievements(currentUser.uid, userVillage);
        if (newlyUnlocked.length > 0) {
          const text = newlyUnlocked.length === 1 ? t.achievementSingle : t.achievementMultiple(newlyUnlocked.length);
          setAchievementBannerText(text);
          setShowAchievementBanner(true);
        }
      } catch (e) {}
    };
    checkVillage();
  }, [currentUser?.uid, userVillage, t]);

  useEffect(() => {
    if (showAchievementBanner) {
      bannerTimerRef.current = setTimeout(() => setShowAchievementBanner(false), 90000);
    }
    return () => clearTimeout(bannerTimerRef.current);
  }, [showAchievementBanner]);

  useEffect(() => {
    if (currentView === 'homepage') {
      setTimeout(() => {
        if (scrollRef.current) {
          const currentCard = scrollRef.current.querySelector('.day-card.current, .day-card.completed-today');
          if (currentCard) {
            currentCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        }
      }, 500);
    }
  }, [currentView]);

  const handleDailyPlanBack = useCallback(async (dayCompleted) => {
    setCurrentView('homepage');
    setSelectedDay(null);
    if (dayCompleted) {
      await Promise.all([
        refreshUserData(),
        loadHomepageData()
      ]);
      setStreakJustUpdated(true);
    }
  }, [refreshUserData, loadHomepageData]);

  const handleLekhiTrainingBack = useCallback(async (dayCompleted) => {
    setCurrentView('homepage');
    if (dayCompleted) {
      await Promise.all([
        refreshUserData(),
        loadHomepageData()
      ]);
      setStreakJustUpdated(true);
    }
  }, [refreshUserData, loadHomepageData]);

  const handleDiagnosticBack = useCallback(() => {
    setCurrentView('homepage');
  }, []);

  const handleSundayTestBack = useCallback(async (testCompleted) => {
    setCurrentView('homepage');
    if (testCompleted) {
      await Promise.all([
        refreshUserData(),
        loadHomepageData()
      ]);
      setStreakJustUpdated(true);
    }
  }, [refreshUserData, loadHomepageData]);

  const bothDoneToday = !canTrainToday && !canLekhiToday;
  const isSundayMode = instantSundayTest;

  const weekDays = getWeekDays();
  const india = getIndiaDate();
  const todayStr = getIndiaDateString();

  const getFireStatus = (dateStr, date) => {
    if (date > india && dateStr !== todayStr) return 'future';
    if (weekCompletions[dateStr]) return 'completed';
    return 'missed';
  };

  const getCampStatus = (campNumber) => {
    if (attendedCamps.includes(campNumber)) return 'completed';
    if (unlockedCamps.includes(campNumber)) return 'current';
    return 'locked';
  };

  const showCampSection = unlockedCamps.length > 0 || attendedCamps.length > 0;
  const showDiagnosticCard = showCampSection && !diagnosticCompleted;
  const userRankIndex = villageRankings.findIndex(v => v.village === userVillage);
  const top10 = villageRankings.slice(0, 10);
  const userInTop10 = userRankIndex !== -1 && userRankIndex < 10;
  const userVillageData = userRankIndex !== -1 ? villageRankings[userRankIndex] : null;

  const getRankClass = (index) => {
    if (index === 0) return 'leaderboard-rank gold';
    if (index === 1) return 'leaderboard-rank silver';
    if (index === 2) return 'leaderboard-rank bronze';
    return 'leaderboard-rank';
  };

  const handleFitnessClick = () => {
    if (!fitnessUnlocked || !canTrainToday) return;
    setSelectedDay(fitnessCurrentDay);
    setCurrentView('dailyplan');
  };

  const handleLekhiClick = () => {
    if (!lekhiUnlocked || !canLekhiToday) return;
    setCurrentView('lekhitraining');
  };

  const handleSundayTestClick = () => {
    if (sundayTestStatus !== 'available') return;
    setCurrentView('sundaytest');
  };

  const renderPastCard = (entry) => {
    return (
      <div key={entry.date} className="day-card completed past-card">
        <div className="past-card-date">{formatPastDate(entry.date, language)}</div>
        <div className="past-card-status-list">
          <div className="past-card-row">
            <span className={`past-card-icon ${entry.fitnessDone ? 'done' : 'missed'}`}>
              {entry.fitnessDone ? '✓' : '✗'}
            </span>
            <span className="past-card-label">{t.vyayam}</span>
          </div>
          <div className="past-card-row">
            <span className={`past-card-icon ${entry.lekhiDone ? 'done' : 'missed'}`}>
              {entry.lekhiDone ? '✓' : '✗'}
            </span>
            <span className="past-card-label">{t.lekhi}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderFutureCard = (label, key) => {
    return (
      <div key={key} className="day-card locked future-card">
        <div className="future-card-date">{label}</div>
        <div className="future-card-status-list">
          <div className="future-card-row">
            <span className="future-card-icon">🔒</span>
            <span className="future-card-label">{t.vyayam}</span>
          </div>
          <div className="future-card-row">
            <span className="future-card-icon">🔒</span>
            <span className="future-card-label">{t.lekhi}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentCard = () => {
    // Sunday test mode
    if (isSundayMode) {
      // It's confirmed Sunday-test day, but whether the test is already
      // taken vs. available still needs the async check. Show a neutral
      // loading state here — never the Fitness/Lekhi buttons.
      if (sundayTestStatus === null) {
        return (
          <div className="day-card current">
            <div className="current-card-header">{t.todayTraining}</div>
            <div className="status-text">...</div>
          </div>
        );
      }
      const cardStatus = sundayTestStatus === 'completed' ? 'completed-today' : 'current';
      return (
        <div className={`day-card ${cardStatus}`}>
          <div className="current-card-header">{t.todayTraining}</div>
          {sundayTestStatus === 'completed' ? (
            <div className="status-text lekhi-completed">{t.testCompleted}</div>
          ) : (
            <button
              className="sunday-test-button"
              onClick={(e) => { e.stopPropagation(); handleSundayTestClick(); }}
            >
              {t.todaysTest.split('\n').map((line, i) => (
                <span key={i} className="sunday-test-button-line">{line}</span>
              ))}
            </button>
          )}
        </div>
      );
    }

    // Regular weekday mode
    const cardStatus = bothDoneToday ? 'completed-today' : 'current';

    if (bothDoneToday) {
      return (
        <div className={`day-card ${cardStatus}`}>
          <div className="current-card-header">{t.todayTraining}</div>
          <div className="status-text both-completed">{t.bothCompleted}</div>
        </div>
      );
    }

    const fitnessActive = fitnessUnlocked && canTrainToday;
    const lekhiActive = lekhiUnlocked && canLekhiToday;

    return (
      <div className={`day-card ${cardStatus}`}>
        <div className="current-card-header">{t.todayTraining}</div>

        {canTrainToday ? (
          <button
            className={`action-button ${!fitnessActive ? 'disabled' : ''}`}
            onClick={(e) => { e.stopPropagation(); handleFitnessClick(); }}
            disabled={!fitnessActive}
          >
            <span className="action-button-label">{t.vyayam}</span>
            <span className="action-button-day">{t.day} {fitnessCurrentDay}</span>
          </button>
        ) : (
          <div className="status-text fitness-completed">{t.fitnessCompleted}</div>
        )}

        {canLekhiToday ? (
          <button
            className={`lekhi-button ${!lekhiActive ? 'disabled' : ''}`}
            onClick={(e) => { e.stopPropagation(); handleLekhiClick(); }}
            disabled={!lekhiActive}
          >
            <span className="action-button-label">{t.lekhi}</span>
            <span className="action-button-day">
              {lekhiNeedsLevelPick ? t.chooseLevel : `${t.day} ${lekhiCurrentDay}`}
            </span>
          </button>
        ) : (
          <div className="status-text lekhi-completed">{t.lekhiCompleted}</div>
        )}
      </div>
    );
  };

  if (currentView === 'campplan') {
    return (
      <CampPlanPage
        campNumber={selectedCamp}
        onBack={() => {
          setCurrentView('homepage');
          setSelectedCamp(null);
        }}
      />
    );
  }

  if (currentView === 'dailyplan') {
    return (
      <DailyPlanPage
        day={selectedDay}
        onBack={handleDailyPlanBack}
      />
    );
  }

  if (currentView === 'lekhidiagnostic') {
    return <LekhiDiagnosticPage onBack={handleDiagnosticBack} />;
  }

  if (currentView === 'lekhitraining') {
    return <LekhiTodayPage onBack={handleLekhiTrainingBack} />;
  }

  if (currentView === 'sundaytest') {
    return (
      <LekhiSundayTestPage
        testNumber={sundayTestNumber}
        onBack={handleSundayTestBack}
      />
    );
  }

  return (
    <div className="homepage-container">

      {showNotificationPrompt && (
        <NotificationPrompt
          userId={currentUser.uid}
          db={db}
          currentStatus={userData?.notificationPromptStatus || 'never_asked'}
          onClose={handleNotificationClose}
        />
      )}

      {celebrationQueue.length > 0 && (
        <CelebrationPopup
          achievement={celebrationQueue[0]}
          userData={userData}
          language={language}
          onClose={handleCelebrationClose}
        />
      )}

      <div className="homepage-header">
        <div className="header-left">
          <div className="header-greeting">Hi, {firstName}</div>
          <div className="header-village">{userVillage}</div>
        </div>
        <div className="header-center">
          <div className="points-circle">{points}</div>
          <div className="points-label">{t.points}</div>
        </div>
        <button className="lang-toggle" onClick={handleLanguageToggle}>
          {language === 'en' ? 'मराठी' : 'EN'}
        </button>
      </div>

      <div className="homepage-content">

        {showAchievementBanner && (
          <div className="achievement-banner">
            <span className="achievement-banner-text">{achievementBannerText}</span>
            <div className="achievement-banner-actions">
              <button
                className="achievement-banner-view"
                onClick={() => {
                  clearTimeout(bannerTimerRef.current);
                  setShowAchievementBanner(false);
                  if (setActiveTab) setActiveTab('achievements');
                }}
              >
                {t.viewAchievements}
              </button>
              <button
                className="achievement-banner-close"
                onClick={() => {
                  clearTimeout(bannerTimerRef.current);
                  setShowAchievementBanner(false);
                }}
              >✕</button>
            </div>
          </div>
        )}

        <div className="motivational-message">
          {motivationalMessage[language] || motivationalMessage.mr}
        </div>

        <div className="streak-section">
          <div className="streak-left">
            <span className={`streak-number ${streakJustUpdated ? 'streak-pulse' : ''}`}>
              {displayStreak}
            </span>
            <span className="streak-fire">🔥</span>
          </div>
          <div className="streak-divider" />
          <div className="streak-days">
            {weekDays.map((d, i) => {
              const status = getFireStatus(d.dateStr, d.date);
              return (
                <div key={i} className="streak-day">
                  <span className={`streak-day-icon ${status}`}>🔥</span>
                  <span className="streak-day-label">{d.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="training-path">
          <div className="path-container" ref={scrollRef}>
            {pastCompletions.map(entry => renderPastCard(entry))}
            {renderCurrentCard()}
            {renderFutureCard(t.tomorrow, 'tomorrow')}
            {renderFutureCard(t.dayAfter, 'dayafter')}
          </div>
        </div>

        {showCampSection && (
          <div className="camp-pathway">
            <div className="camp-container">
              {showDiagnosticCard && (
                <>
                  <div
                    className="diagnostic-card"
                    onClick={() => setCurrentView('lekhidiagnostic')}
                  >
                    {t.diagnostic.split(' ').map((word, i) => (
                      <div key={i}>{word}</div>
                    ))}
                  </div>
                  <div className="diagnostic-card-separator" />
                </>
              )}
              {[1,2,3,4,5,6,7,8,9,10].map((campNumber) => {
                const status = getCampStatus(campNumber);
                return (
                  <div
                    key={campNumber}
                    className={`camp-circle ${status}`}
                    onClick={() => {
                      if (status === 'current') {
                        setSelectedCamp(campNumber);
                        setCurrentView('campplan');
                      }
                    }}
                  >
                    {t.camp} {campNumber}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="leaderboard-section">
          <div className="leaderboard-tabs">
            <button
              className={`leaderboard-tab leaderboard-tab-village ${leaderboardView === 'village' ? 'active' : ''}`}
              onClick={() => handleLeaderboardToggle('village')}
            >
              {t.villageRankings}
            </button>
            <button
              className={`leaderboard-tab leaderboard-tab-streak ${leaderboardView === 'streak' ? 'active' : ''}`}
              onClick={() => handleLeaderboardToggle('streak')}
            >
              🔥 {t.streakRankings}
            </button>
          </div>

          {leaderboardView === 'village' ? (
            loadingRankings ? (
              <div className="leaderboard-loading">...</div>
            ) : villageRankings.length === 0 ? (
              <div className="leaderboard-empty">No village data yet</div>
            ) : (
              <div className="leaderboard-list">
                {top10.map((village, index) => (
                  <div
                    key={village.village}
                    className={`leaderboard-row${village.village === userVillage ? ' user-village' : ''}`}
                  >
                    <span className={getRankClass(index)}>{index + 1}</span>
                    <span className="leaderboard-name">{village.village}</span>
                    <span className="leaderboard-points">{village.points} {t.pts}</span>
                  </div>
                ))}
                {!userInTop10 && userVillageData && (
                  <div className="leaderboard-row user-village user-pinned">
                    <span className="leaderboard-rank">{userRankIndex + 1}</span>
                    <span className="leaderboard-name">{userVillageData.village}</span>
                    <span className="leaderboard-points">{userVillageData.points} {t.pts}</span>
                  </div>
                )}
              </div>
            )
          ) : (
            loadingStreakLeaderboard ? (
              <div className="leaderboard-loading">...</div>
            ) : !streakLeaderboard || streakLeaderboard.list.length === 0 ? (
              <div className="leaderboard-empty">No streak data yet</div>
            ) : (
              <div className="leaderboard-list">
                {streakLeaderboard.list.map((u, index) => (
                  <div
                    key={u.id}
                    className={`leaderboard-row${u.id === currentUser?.uid ? ' user-village' : ''}`}
                  >
                    <span className={getRankClass(index)}>{index + 1}</span>
                    <span className="leaderboard-name">{formatStreakName(u)}</span>
                    <span className="leaderboard-points">{u.streakCount || 0} days🔥</span>
                  </div>
                ))}
                {!streakLeaderboard.currentUserInList && streakLeaderboard.currentUserRank && (
                  <div className="leaderboard-row user-village user-pinned">
                    <span className="leaderboard-rank">{streakLeaderboard.currentUserRank}</span>
                    <span className="leaderboard-name">{formatStreakName(userData || {})}</span>
                    <span className="leaderboard-points">{userData?.streakCount || 0} days🔥</span>
                  </div>
                )}
              </div>
            )
          )}
        </div>

      </div>
    </div>
  );
};

export default Homepage;